import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { router } from '@inertiajs/react';
import api from '../lib/api';
import Sidebar from '../components/Wiki/Sidebar';
import {
  focusById,
  SIDEBAR_COLLAPSED_KEY,
  UNTITLED,
  WIKI_SEARCH_INPUT_ID,
  WikiShellContext,
} from '../components/Wiki/shell';

// wiki 的骨架：左边栏 + 自己滚动的文档区。**满屏，没有全站 `Header`。**
//
// 改版前这里是「Header + 居中 820px 单列 + 整页滚动」。换掉的理由是设计稿
// （docs/design/wiki-ui-mockup.html）那条：wiki 是**待着**的地方，不是一次性
// 打开一篇就走。待着就需要一个常驻的导航面 —— 最近编辑、标签、最久没动、
// 回收站在单列版式里全都只能塞进列表页的正文区，于是从任何一篇文章里都看不见
// 它们，回列表页是唯一的导航动作。
//
// **为什么连 Header 都不留**（2026-08-26 第二轮，用户「按那个UI来」）：稿子画的
// 就是 100vh 两栏、顶部没有横条。留着 Header 的话屏幕上会有两条横杠叠着
// （64px + 45px 顶条），而它们讲的是同一类事情（我在哪、我能去哪）。
//
// ⚠️ 代价必须由边栏兜住：`Header` 是番茄钟 / 规划 / 统计 / 设置 / 接入 / 后台
// 的**唯一**入口（`Header.tsx` 的注释写着「删掉就等于让页面失联」）。所以
// `Wiki/Sidebar.tsx` 摊了三个高频入口，其余全部收进品牌块那个 `⌄`
// （`Wiki/BrandMenu.tsx`）—— 一项都没少，`test/frontend_nav_coverage_test.rb`
// 钉住这一条。
//
// 也不用 AppLayout：它的 container mx-auto p-4 之外还带全局 TimerBar 和 Footer，
// 那两样在一篇文档上面/下面都没有落点。
//
// 顶条不在这里，在页面组件里（`components/Wiki/TopBar.tsx`）——面包屑的第二段
// 和右侧那几个按钮是每一页自己的东西，而 Inertia 的 layout 函数拿不到页面的
// props。
export default function WikiLayout({ children }: { children: ReactNode }) {
  // 初值从 localStorage 读，用惰性初始化：这个组件在 SSR 下也会跑，直接在
  // 函数体里读 localStorage 会炸。
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newPageBusy, setNewPageBusy] = useState(false);
  const [newPageError, setNewPageError] = useState<string | null>(null);
  // 「有一次建页在飞」这个事实用 ref 而不是上面那个 state 来守。
  //
  // 用 state 守就得把它放进 useCallback 的依赖里，于是 startNewPage 的身份每次
  // busy 翻转都变一次，白白让整个 shell context 换一份。ref 读到的是当下的值，
  // 而这里要的就是「当下有没有在飞」。
  const inFlight = useRef(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      }
      return next;
    });
  }, []);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // 去搜索。搜索框内嵌在列表页里（components/Wiki/SearchBar），所以这个动作是
  // 「必要时先回列表页，然后把光标送进那个框」。
  //
  // 已经在列表页时**不导航**：那会把当前的筛选条件冲掉，而人按 ⌘K 的意思通常是
  // 「我要改一下刚才搜的东西」，不是「重来」。
  const focusSearch = useCallback(() => {
    const focus = () => focusById(WIKI_SEARCH_INPUT_ID);
    if (window.location.pathname === '/wiki') {
      focus();
      return;
    }
    router.visit('/wiki', { onFinish: focus });
  }, []);

  // 建一页，然后跳到那一页的编辑态。**建页只有这一份实现**，见 shell.ts 里
  // `startNewPage` 的注释。
  //
  // 为什么不能像 `/wiki/new` 那样「先填表单再一次 POST」：正文粘图走
  // `POST /web/internal/wiki_pages/:url_key/images`，附件挂在**页面记录**上。
  // 页面还不存在就没有 url_key，那个端点根本打不出去 —— 于是「先建记录，再进
  // 编辑态写正文」不是偏好，是被图片这条路定死的。
  //
  // 查询串而不是 Inertia 的 state：后者在整页导航后就没了，而这一跳是
  // router.visit（照 WikiPage.tsx 里 ?edit=1 的先例）。
  //   没给标题 → ?new=1  → 编辑态，光标在标题上并全选
  //   给了标题 → ?edit=1 → 编辑态，光标在正文（标题已知）
  const startNewPage = useCallback(async (title?: string) => {
    if (inFlight.current) return;

    const named = title?.trim();
    inFlight.current = true;
    setNewPageBusy(true);
    setNewPageError(null);
    try {
      const page = await api.wikiPages.create(named || UNTITLED);
      router.visit(`/wiki/${page.url_key}?${named ? 'edit' : 'new'}=1`);
    } catch (cause) {
      // 没有 catch 就只剩一个 unhandled rejection：按钮恢复可点、界面上零提示。
      // 会话过期（401）、CSRF 失效（422）、标题超 200 字都走这条路。
      // 只放原因，别把「建页没成功」也写进来 —— 横幅自己带那句前缀。
      setNewPageError(cause instanceof Error ? cause.message : '原因不明，再试一次。');
    } finally {
      inFlight.current = false;
      setNewPageBusy(false);
    }
  }, []);

  const dismissNewPageError = useCallback(() => setNewPageError(null), []);

  // ⌘K / Ctrl+K 全局起效。绑在 document 上而不是某个元素：这个快捷键的意义就是
  // 「不管光标在哪都能用」——包括正在读一篇文章的时候。
  //
  // preventDefault 是必须的：Chrome 把 ⌘K 给了地址栏的搜索模式，不拦就是
  // 「按了之后光标跳去地址栏」，而页面上的搜索框同时也拿到了焦点 —— 两处抢。
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        focusSearch();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [focusSearch]);

  const shell = useMemo(
    () => ({
      collapsed,
      toggleCollapsed,
      openDrawer,
      focusSearch,
      startNewPage,
      newPageBusy,
      newPageError,
      dismissNewPageError,
    }),
    [
      collapsed,
      toggleCollapsed,
      openDrawer,
      focusSearch,
      startNewPage,
      newPageBusy,
      newPageError,
      dismissNewPageError,
    ],
  );

  return (
    <WikiShellContext.Provider value={shell}>
      <div className="flex h-screen flex-col overflow-hidden bg-wiki-doc">
        {/*
          两栏。左 260px 固定，右自适应并**自己滚动**。

          不是三栏：这一期没有页面树（parent_id 在 §17.2 被砍），也没有第三栏
          该装的东西 —— 右侧目录是正文页内部的一个浮动块（Toc.tsx），不是布局
          的一栏。

          ⚠️ 列宽写在**这里**，不写在页面组件里。/todo 那侧的列宽写在四个页面
          各自的 grid 类名上，于是 test/layout_consistency_test.rb 需要三条断言
          去钉「两页的侧栏列宽相等」（曾经真的一页 177px 一页 252px，而 Sidebar
          组件一个字都没改）。列宽只有一处定义时那个坑构造上不存在，比加测试好。
        */}
        <div
          className={[
            'grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)]',
            collapsed ? 'sm:grid-cols-[0_minmax(0,1fr)]' : 'sm:grid-cols-[260px_minmax(0,1fr)]',
          ].join(' ')}
        >
          <Sidebar open={drawerOpen} onClose={closeDrawer} />
          {/* min-w-0：不给的话文档区里任何一个宽元素（长表格、不换行的代码块）
              都会把这一列撑过 1fr，边栏跟着被挤窄。 */}
          <main className="min-w-0 overflow-y-auto bg-wiki-doc">
            {/* 建页失败的落点。渲染在**这一层**是因为动作是外壳级的：两个触发点
                （顶条的 ＋ 在页面里、边栏那一行在边栏里）没有共同的内联落点，
                而 `TopBar` 是每个页面自己渲染的，塞进去等于四个页面各写一遍。

                样式照 `Wiki/Editor.tsx` 的冲突横幅。仓库里没有 wiki 能用的
                toast —— `message.error` 是 antd 的，而 /todo 与 wiki 刻意把 antd
                挡在外面。

                在顶条**上面**：`TopBar` 在 children 里，layout 这一层插不到它
                后面去。横幅会随滚动离开，而顶条是 sticky 的，两者不打架。 */}
            {newPageError && (
              <div
                className="mx-3.5 mt-3 flex items-start gap-2 rounded-lg border
                           border-todo-warnLine bg-todo-warn px-3 py-2.5 text-[13px]
                           leading-relaxed text-todo-ink2"
              >
                <span className="min-w-0 flex-1">建页没成功：{newPageError}</span>
                <button
                  type="button"
                  onClick={dismissNewPageError}
                  aria-label="关掉这条提示"
                  className="shrink-0 text-todo-muted hover:text-todo-ink2"
                >
                  ✕
                </button>
              </div>
            )}
            {children}
          </main>
        </div>
      </div>
    </WikiShellContext.Provider>
  );
}

// 正文列。设计稿：Notion 的正文列宽 708px，这里 720px —— 中文行长比英文更吃
// 宽度，再宽一行就超过 45 个字。
//
// 导出成常量而不是让四个页面各写一遍：列宽是这套版式的核心尺寸，四处写散了
// 就会出现「历史页的正文比正文页宽 40px」这种没人说得清是不是故意的差别。
export const DOC = 'mx-auto w-full max-w-[720px] px-6 pb-40 pt-10';
// 版本历史那一页是双栏（列表 + 预览），720px 装不下，用设计稿的 .doc.wide。
export const DOC_WIDE = 'mx-auto w-full max-w-[1100px] px-6 pb-40 pt-10';
